// Promise.withResolvers polyfill 测试和强制注入工具
// 用于确保在所有浏览器环境中都能正确使用 Promise.withResolvers

/**
 * 强制注入 Promise.withResolvers polyfill
 * 这个函数会在运行时检查并强制注入 polyfill
 */
export function ensurePromiseWithResolvers(): void {
  console.log('[POLYFILL TEST] Checking Promise.withResolvers availability...');
  
  // 获取全局 Promise 构造函数
  const globalScope = (function() {
    if (typeof globalThis !== 'undefined') return globalThis;
    if (typeof window !== 'undefined') return window;
    if (typeof global !== 'undefined') return global;
    if (typeof self !== 'undefined') return self;
    throw new Error('Unable to locate global object');
  })();
  
  const PromiseConstructor = globalScope.Promise;
  
  if (!PromiseConstructor) {
    throw new Error('Promise constructor not found in global scope');
  }
  
  // 检查是否已存在 withResolvers 方法
  if (typeof PromiseConstructor.withResolvers === 'function') {
    console.log('[POLYFILL TEST] Promise.withResolvers is already available');
    return;
  }
  
  console.log('[POLYFILL TEST] Promise.withResolvers not found, injecting polyfill...');
  
  // 定义 polyfill 实现
  const withResolversImpl = function<T = any>() {
    let resolve: (value: T | PromiseLike<T>) => void;
    let reject: (reason?: any) => void;
    
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    
    return {
      promise,
      resolve: resolve!,
      reject: reject!
    };
  };
  
  // 尝试多种方式注入 polyfill
  let injectionSuccess = false;
  
  // 方法1: 直接赋值
  try {
    (PromiseConstructor as any).withResolvers = withResolversImpl;
    if (typeof PromiseConstructor.withResolvers === 'function') {
      injectionSuccess = true;
      console.log('[POLYFILL TEST] Direct assignment successful');
    }
  } catch (e) {
    console.warn('[POLYFILL TEST] Direct assignment failed:', e);
  }
  
  // 方法2: 使用 Object.defineProperty
  if (!injectionSuccess) {
    try {
      Object.defineProperty(PromiseConstructor, 'withResolvers', {
        value: withResolversImpl,
        writable: true,
        configurable: true,
        enumerable: false
      });
      if (typeof PromiseConstructor.withResolvers === 'function') {
        injectionSuccess = true;
        console.log('[POLYFILL TEST] Object.defineProperty successful');
      }
    } catch (e) {
      console.warn('[POLYFILL TEST] Object.defineProperty failed:', e);
    }
  }
  
  // 方法3: 使用 Reflect.defineProperty (如果可用)
  if (!injectionSuccess && typeof Reflect !== 'undefined' && Reflect.defineProperty) {
    try {
      Reflect.defineProperty(PromiseConstructor, 'withResolvers', {
        value: withResolversImpl,
        writable: true,
        configurable: true,
        enumerable: false
      });
      if (typeof PromiseConstructor.withResolvers === 'function') {
        injectionSuccess = true;
        console.log('[POLYFILL TEST] Reflect.defineProperty successful');
      }
    } catch (e) {
      console.warn('[POLYFILL TEST] Reflect.defineProperty failed:', e);
    }
  }
  
  // 验证注入结果
  if (injectionSuccess) {
    console.log('[POLYFILL TEST] Promise.withResolvers polyfill successfully injected');
    
    // 测试 polyfill 功能
    try {
      const { promise, resolve, reject } = PromiseConstructor.withResolvers();
      resolve('test');
      promise.then(() => {
        console.log('[POLYFILL TEST] Polyfill functionality test passed');
      }).catch((error) => {
        console.error('[POLYFILL TEST] Polyfill functionality test failed:', error);
      });
    } catch (error) {
      console.error('[POLYFILL TEST] Polyfill functionality test error:', error);
    }
  } else {
    console.error('[POLYFILL TEST] Failed to inject Promise.withResolvers polyfill');
    throw new Error('Unable to inject Promise.withResolvers polyfill');
  }
}

/**
 * 测试 Promise.withResolvers 是否可用
 */
export function testPromiseWithResolvers(): boolean {
  try {
    const globalScope = (function() {
      if (typeof globalThis !== 'undefined') return globalThis;
      if (typeof window !== 'undefined') return window;
      if (typeof global !== 'undefined') return global;
      if (typeof self !== 'undefined') return self;
      return null;
    })();
    
    if (!globalScope || !globalScope.Promise) {
      return false;
    }
    
    return typeof globalScope.Promise.withResolvers === 'function';
  } catch (error) {
    console.error('[POLYFILL TEST] Error testing Promise.withResolvers:', error);
    return false;
  }
}